import {
  AdminPaintSchemeFormValue,
  AdminPaintSchemeGridRow,
  EMPTY_ADMIN_PAINT_SCHEME_FORM,
  HEMPEL_TIER_OPTIONS,
  PAINT_PERFORMANCE_INDEXES,
  PaintPerformanceIndex,
} from "../../models/admin-paint-scheme.models";
import { toAdminPaintSchemeFormValue } from "../../utils/admin-paint-scheme.utils";
import { Dialog } from "@headlessui/react";
import {
  ArrowPathIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { FormEvent, useEffect, useState } from "react";

interface Props {
  open: boolean;
  record: AdminPaintSchemeGridRow | null;
  saving: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onSave: (value: AdminPaintSchemeFormValue) => void | Promise<void>;
}

const fieldClassName =
  "h-11 rounded-md border border-gray-300 bg-white px-3 text-sm font-normal text-gray-900 shadow-sm focus:border-green focus:outline-none focus:ring-2 focus:ring-green/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white";

const AdminPaintSchemeModal = ({ open, record, saving, errorMessage, onCancel, onSave }: Props) => {
  const [form, setForm] = useState<AdminPaintSchemeFormValue>(EMPTY_ADMIN_PAINT_SCHEME_FORM);

  useEffect(() => {
    if (open) setForm(record ? toAdminPaintSchemeFormValue(record) : EMPTY_ADMIN_PAINT_SCHEME_FORM);
  }, [open, record]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void Promise.resolve(onSave(form));
  };

  return (
    <Dialog open={open} onClose={() => !saving && onCancel()} className="relative z-[1000]">
      <div className="fixed inset-0 bg-slate-950/55 backdrop-blur-[2px]" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
        <div className="flex min-h-full items-center justify-center">
          <Dialog.Panel
            as="form"
            onSubmit={submit}
            data-test="admin-paint-scheme-form"
            className="w-full max-w-4xl overflow-hidden rounded-2xl border border-[#D7E2DE] bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
          >
            <div className="border-greyscale-20 flex items-start justify-between gap-4 border-b bg-[#F7FBFA] px-5 py-5 dark:border-gray-700 dark:bg-gray-800 sm:px-6">
              <div>
                <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white">
                  {record ? "Edit Paint" : "Add a Paint"}
                </Dialog.Title>
                <div className="mt-3 inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-full bg-primary-10 px-3 py-1.5 text-xs font-medium text-green dark:bg-teal-950/50 dark:text-teal-200">
                  <InformationCircleIcon className="h-4 w-4 shrink-0" />
                  <span>
                    {record ? "Update the maintained paint details" : "Add a maintained vendor and paint combination"}
                  </span>
                  <span className="text-[#8AA7A2]" aria-hidden="true">
                    •
                  </span>
                  <span>
                    <RequiredAsterisk /> Required fields
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={onCancel}
                disabled={saving}
                aria-label="Close dialog"
                className="border-greyscale-20 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border bg-white text-gray-500 transition-colors hover:bg-greyscale-10 hover:text-gray-900 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {errorMessage && (
              <div className="mx-5 mt-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200 sm:mx-6">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <div className="grid grid-cols-1 gap-x-5 gap-y-5 px-5 py-6 sm:px-6 md:grid-cols-2">
              <Field label="Vendor" required>
                <input
                  autoFocus
                  required
                  value={form.vendor}
                  onChange={(event) => setForm((current) => ({ ...current, vendor: event.target.value }))}
                  className={fieldClassName}
                  data-test="admin-paint-scheme-vendor"
                />
              </Field>
              <Field label="Paint" required>
                <input
                  required
                  value={form.paint}
                  onChange={(event) => setForm((current) => ({ ...current, paint: event.target.value }))}
                  className={fieldClassName}
                  data-test="admin-paint-scheme-paint"
                />
              </Field>
              <Field label="Performance Index" required>
                <select
                  required
                  value={form.performanceIndex}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      performanceIndex: event.target.value as PaintPerformanceIndex,
                    }))
                  }
                  className={fieldClassName}
                  data-test="admin-paint-scheme-performance-index"
                >
                  {PAINT_PERFORMANCE_INDEXES.map((index) => (
                    <option key={index} value={index}>
                      {index}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Hempel Tier" required>
                <select
                  required
                  value={form.hempelTier}
                  onChange={(event) => setForm((current) => ({ ...current, hempelTier: event.target.value }))}
                  className={fieldClassName}
                  data-test="admin-paint-scheme-hempel-tier"
                >
                  {HEMPEL_TIER_OPTIONS.map((tier) => (
                    <option key={tier} value={tier}>
                      {tier}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="md:col-span-2">
                <Field label="Comments">
                  <textarea
                    rows={4}
                    value={form.comments}
                    onChange={(event) => setForm((current) => ({ ...current, comments: event.target.value }))}
                    className="min-h-[100px] w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm font-normal text-gray-900 shadow-sm focus:border-green focus:outline-none focus:ring-2 focus:ring-green/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                    data-test="admin-paint-scheme-comments"
                  />
                </Field>
              </div>
            </div>

            <div className="border-greyscale-20 flex flex-col-reverse gap-3 border-t bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/40 sm:flex-row sm:justify-end sm:px-6">
              <button
                type="button"
                onClick={onCancel}
                disabled={saving}
                className="inline-flex h-11 items-center justify-center rounded-md border border-gray-300 bg-white px-5 text-sm font-semibold text-lightGrey transition-colors hover:bg-greyscale-10 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-green px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#176a5e] disabled:cursor-not-allowed disabled:opacity-50"
                data-test="admin-paint-scheme-save-confirm"
              >
                {saving ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                {saving ? "Saving..." : "Save record"}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

const RequiredAsterisk = () => (
  <sup className="ml-1 text-[#BF0002]" aria-hidden="true">
    *
  </sup>
);

const Field = ({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) => (
  <label className="flex flex-col gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
    <span>
      {label}
      {required && <RequiredAsterisk />}
    </span>
    {children}
  </label>
);

export default AdminPaintSchemeModal;
